import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import FeedbackModal from '../components/common/FeedbackModal';

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState(null);

  const clear = useCallback(() => {
    setNotification(null);
  }, []);

  const show = useCallback((message, options = {}) => {
    if (!message) return;
    const {
      type = 'info',
      autoClose = 3000,
      title = '',
      confirmText = 'OK',
      showCloseButton = true
    } = options;

    setNotification({
      id: Date.now(),
      type,
      message,
      autoClose,
      title,
      confirmText,
      showCloseButton,
    });
  }, []);

  const success = useCallback((message, options = {}) => {
    show(message, { ...options, type: 'success' });
  }, [show]);

  const error = useCallback((message, options = {}) => {
    show(message, { ...options, type: 'error' });
  }, [show]);

  const warning = useCallback((message, options = {}) => {
    show(message, { ...options, type: 'warning' });
  }, [show]);

  const info = useCallback((message, options = {}) => {
    show(message, { ...options, type: 'info' });
  }, [show]);

  const contextValue = useMemo(() => ({
    show,
    success,
    error,
    warning,
    info,
    clear
  }), [show, success, error, warning, info, clear]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      {notification && (
        <FeedbackModal
          key={notification.id}
          isOpen={true}
          type={notification.type}
          title={notification.title}
          message={notification.message}
          confirmText={notification.confirmText}
          showCancel={false}
          showCloseButton={notification.showCloseButton}
          autoClose={notification.autoClose}
          onClose={clear}
          onConfirm={clear}
        />
      )}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used inside NotificationProvider');
  }
  return context;
};
